import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    AlertTriangle, CheckCircle2, Clock, Copy, ExternalLink, FileText, History,
    MessageSquareText, ShieldCheck, X, XCircle,
} from 'lucide-react';
import {
    SolicitacaoCancelamentoDetalhada, MOTIVO_LABEL, STATUS_SOLICITACAO_LABEL, MEIO_RESPOSTA_LABEL,
} from '../../src/lib/cancelamentos';
import type { TipoRetornoCliente, MeioRespostaManual } from '../../src/lib/cancelamentos';
import { estadoPrazoRetorno, diasDeAtraso, ORIGEM_SOLICITACAO_LABEL } from '../../src/lib/prazoRetornoCancelamento';
import { STATUS_RATEIO_UC_META } from '../../src/lib/statusRateioUc';
import { situacaoEnvio } from '../../src/lib/envioCancelamento';
import type { ItemEnvio } from '../../src/lib/envioCancelamento';
import { acoesDisponiveis, dataSolicitacaoExibida, estadoRetornoCliente, RETORNO_CLIENTE_LABEL, DIAS_ALERTA_SEM_RETORNO_CLIENTE } from '../../src/lib/cancelamentosLista';
import {
    fmtBRL, fmtData, fmtKwh, STATUS_SOLICITACAO_CLS, DISTRATO_META, DISTRATO_EXTERNO_LABEL,
    RETORNO_META, SELO, linkGerenciarUc, multaNaoCobrada,
} from './cancelamentosVisual';

/**
 * Tudo de uma solicitação de cancelamento num lugar só (#6121).
 *
 * Antes eram 13 colunas numa tabela que não cabia na tela: para ver distrato,
 * prazos e status a pessoa rolava para o lado e perdia o nome do cliente. A lista
 * ficou com o essencial e o resto mora aqui, junto com as ações.
 *
 * Fica ABERTO depois de uma ação: quem chama passa a solicitação já recarregada
 * da lista, então o modal mostra o estado novo (ex.: validou → aparece "Gerar
 * distrato") sem obrigar a achar a linha de novo. Os modais das ações (validar,
 * responder, distrato, confirmação) abrem por cima — este fica em z-[90], eles
 * em z-[100] ou mais.
 */
interface Props {
    solicitacao: SolicitacaoCancelamentoDetalhada;
    envios: ItemEnvio[] | undefined;
    hojeBrasilia: string;
    /** Algum modal de ação está aberto por cima: o Esc é dele, não deste. */
    bloqueado: boolean;
    onClose: () => void;
    onValidar: () => void;
    onGerarDistrato: () => void;
    onResponder: () => void;
    onHistorico: (anchor: DOMRect) => void;
    onConcluir: () => void;
    onDesistir: () => void;
    onCopiarProtocolo: (protocolo: string) => void;
    /** Registra o retorno do cliente (#6127). Resolve quando gravou; o pai recarrega a lista. */
    onRegistrarRetorno: (tipo: TipoRetornoCliente, retornoEm: string, obs: string) => Promise<void>;
}

const hojeSP = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });

function Campo({ rotulo, children, largo }: { rotulo: string; children: React.ReactNode; largo?: boolean }) {
    return (
        <div className={`min-w-0 ${largo ? 'col-span-full' : ''}`}>
            <dt className="text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">{rotulo}</dt>
            <dd className="mt-1 text-sm text-gray-800 dark:text-slate-100">{children}</dd>
        </div>
    );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
    return (
        <section className="bg-white dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-700 p-5">
            <h3 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-4">{titulo}</h3>
            <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">{children}</dl>
        </section>
    );
}

const SolicitacaoCancelamentoModal: React.FC<Props> = ({
    solicitacao: s, envios, hojeBrasilia, bloqueado, onClose, onValidar, onGerarDistrato, onResponder,
    onHistorico, onConcluir, onDesistir, onCopiarProtocolo, onRegistrarRetorno,
}) => {
    // Formulário do retorno do cliente (#6127), aberto dentro do próprio modal.
    const [registrandoRetorno, setRegistrandoRetorno] = useState(false);
    const [tipoRetorno, setTipoRetorno] = useState<TipoRetornoCliente | ''>('');
    const [dataRetorno, setDataRetorno] = useState(hojeSP());
    const [obsRetorno, setObsRetorno] = useState('');
    const [salvandoRetorno, setSalvandoRetorno] = useState(false);
    useEffect(() => { setRegistrandoRetorno(false); setTipoRetorno(''); setDataRetorno(hojeSP()); setObsRetorno(''); }, [s.id]);
    const fecharRef = useRef<HTMLButtonElement>(null);

    // Esc fecha — mas só quando nenhum modal de ação está por cima; senão um Esc
    // no "Validar" fecharia os dois de uma vez.
    useEffect(() => {
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape' && !bloqueado) onClose(); };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [bloqueado, onClose]);

    useEffect(() => { fecharRef.current?.focus(); }, []);

    const env = situacaoEnvio(envios);
    const dExib = dataSolicitacaoExibida(s, hojeBrasilia);
    const retorno = estadoRetornoCliente(s, hojeBrasilia);
    // Distrato da UC anterior ao pedido (#6128). Com a data do pedido "a conferir"
    // não dá para afirmar nada — o aviso ficaria culpando o distrato pela data errada.
    const distratoAnteriorAoPedido = !!s.distratoDaUcEmitidoEm && !dExib.conferir
        && new Date(s.distratoDaUcEmitidoEm).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) < dExib.iso;
    const dataMinRetorno = s.respostaEnviadaEm
        ? new Date(s.respostaEnviadaEm).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : undefined;
    const salvarRetorno = async () => {
        if (!tipoRetorno || (tipoRetorno === 'outro' && !obsRetorno.trim())) return;
        setSalvandoRetorno(true);
        try {
            await onRegistrarRetorno(tipoRetorno, `${dataRetorno}T12:00:00-03:00`, obsRetorno);
            setRegistrandoRetorno(false);
        } finally {
            setSalvandoRetorno(false);
        }
    };
    const importado = s.origem === 'importacao';
    // Prazos só são calculados quando a solicitação nasce pela tela; os
    // importados nunca tiveram (#6122) — "—" parecia defeito.
    const prazo = (v: string | null) => v ? fmtData(v)
        : importado ? <span className="text-gray-400 dark:text-slate-500">Não calculado (registro importado)</span> : '—';
    const acoes = acoesDisponiveis(s, env);
    const rateioMeta = STATUS_RATEIO_UC_META[s.rateioStatus];
    const distratoMeta = DISTRATO_META[s.distratoStatus];
    const estadoRetorno = estadoPrazoRetorno({ dataRetornoPrevista: s.dataRetornoPrevista, status: s.status, validadoEm: s.validadoEm }, hojeBrasilia);
    const retornoMeta = RETORNO_META[estadoRetorno];
    const atraso = estadoRetorno === 'vencida' ? diasDeAtraso(s.dataRetornoPrevista, hojeBrasilia) : 0;

    const botaoAcao = 'inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors';

    return createPortal(
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="solicitacao-cancelamento-titulo"
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-100 dark:border-slate-700"
                onClick={e => e.stopPropagation()}
            >
                <div className="px-6 py-5 border-b border-gray-100 dark:border-slate-700 flex items-start justify-between gap-4 bg-gray-50/60 dark:bg-slate-800 shrink-0">
                    <div className="min-w-0">
                        <h2 id="solicitacao-cancelamento-titulo" className="text-lg font-bold text-gray-900 dark:text-white truncate">{s.cliente_nome}</h2>
                        <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                            UC {s.uc_numero} · solicitado em {fmtData(dExib.iso)}
                            {dExib.ajuste ? (
                                <span title={`Gravada como ${fmtData(dExib.original)} — ajustada pelo mês da aba da planilha. O banco e a exportação continuam com a data original.`}
                                    className="ml-1.5 text-xs text-gray-400 dark:text-slate-500 cursor-help">(ajustada pela planilha)</span>
                            ) : null}
                            {dExib.conferir ? (
                                <span title="Data no futuro que o mês da aba da planilha não explica — conferir na planilha original."
                                    className="ml-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400 cursor-help">· data a conferir</span>
                            ) : null}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            <span className={`text-[11px] font-bold px-2 py-1 rounded-md border ${STATUS_SOLICITACAO_CLS[s.status]}`}>{STATUS_SOLICITACAO_LABEL[s.status]}</span>
                            {s.origem !== 'sistema' ? (
                                <span className={`${SELO} bg-gray-100 dark:bg-slate-900 text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-700`}>
                                    {s.origem === 'importacao' ? 'Importado' : ORIGEM_SOLICITACAO_LABEL[s.origem]}
                                </span>
                            ) : null}
                            {s.situacaoDivergente ? (
                                <span title="A planilha trouxe como cancelada, mas a UC continua ativa no cadastro — conferir situação real."
                                    className={`${SELO} bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50`}>
                                    <AlertTriangle size={9} /> Divergente
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <a href={linkGerenciarUc(s.uc_numero)} title="Abre o cadastro desta UC — histórico completo do distrato e detalhes do rateio"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-xs font-semibold text-gray-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-colors">
                            <ExternalLink size={12} /> Gerenciar UC
                        </a>
                        <button ref={fecharRef} onClick={onClose} aria-label="Fechar"
                            className="p-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-slate-900/40 p-6 space-y-4">
                    {acoes.podeResponder && acoes.rotuloResposta === 'reenviar' ? (
                        <div className="flex items-start gap-2 rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                            <span>{env.explicacao}</span>
                        </div>
                    ) : null}

                    <Secao titulo="Pedido">
                        <Campo rotulo="Protocolo">
                            {s.protocolo ? (
                                <span className="inline-flex items-center gap-1">
                                    <span className="font-mono font-semibold select-all">{s.protocolo}</span>
                                    <button onClick={() => onCopiarProtocolo(s.protocolo!)} aria-label={`Copiar protocolo ${s.protocolo}`} title="Copiar protocolo"
                                        className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800">
                                        <Copy size={12} />
                                    </button>
                                </span>
                            ) : <span className="text-gray-400 dark:text-slate-500" title="Solicitação registrada antes de o protocolo existir">—</span>}
                        </Campo>
                        <Campo rotulo="Retorno ao cliente">
                            {s.dataRetornoPrevista ? (
                                <span className="inline-flex flex-wrap items-center gap-1.5">
                                    até {fmtData(s.dataRetornoPrevista)}
                                    {retornoMeta ? (
                                        <span className={`${SELO} ${retornoMeta.cls}`}>
                                            {estadoRetorno === 'vencida' ? <AlertTriangle size={9} /> : <Clock size={9} />}
                                            {estadoRetorno === 'vencida' ? `Vencido há ${atraso} dia${atraso === 1 ? '' : 's'}` : retornoMeta.label}
                                        </span>
                                    ) : null}
                                </span>
                            ) : '—'}
                        </Campo>
                        <Campo rotulo="Vendedor">{s.vendedorNome || '—'}</Campo>
                        <Campo rotulo="Motivo">
                            {MOTIVO_LABEL[s.motivo_categoria]}
                            {s.motivo_detalhe ? <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 whitespace-pre-wrap">{s.motivo_detalhe}</p> : null}
                        </Campo>
                        {s.solicitanteNome ? (
                            <Campo rotulo="Quem pediu">
                                {s.solicitanteNome}
                                {s.solicitanteVinculo ? <span className="text-xs text-gray-500 dark:text-slate-400"> · {s.solicitanteVinculo}</span> : null}
                            </Campo>
                        ) : null}
                        {s.observacao ? <Campo rotulo="Observação" largo><span className="whitespace-pre-wrap">{s.observacao}</span></Campo> : null}
                    </Secao>

                    <Secao titulo="Financeiro">
                        <Campo rotulo="Faturas em aberto">
                            {s.faturasEmAbertoQtd > 0 ? (
                                <span className="text-red-600 dark:text-red-400 font-semibold">{s.faturasEmAbertoQtd} · {fmtBRL(s.faturasEmAbertoValor)}</span>
                            ) : s.debitosAbertosValor ? (
                                <span title="Valor informado na importação">{fmtBRL(s.debitosAbertosValor)} <span className="text-xs text-gray-400">informado</span></span>
                            ) : <span className="text-gray-400 dark:text-slate-500">Nenhuma</span>}
                        </Campo>
                        <Campo rotulo="Saldo remanescente">
                            {s.saldoAcumuladoKwh !== null ? fmtKwh(s.saldoAcumuladoKwh) : s.saldoKwhInformado !== null ? (
                                <span title="Saldo informado na importação — a UC não tem fatura recente para calcular.">
                                    {fmtKwh(s.saldoKwhInformado)} <span className="text-xs text-gray-400">informado</span>
                                </span>
                            ) : '—'}
                        </Campo>
                        <Campo rotulo="Multa">
                            {s.multaValor ? fmtBRL(s.multaValor) : '—'}
                            {s.multaSituacao ? (
                                <p className={`text-xs mt-0.5 ${multaNaoCobrada(s.multaSituacao) ? 'text-purple-700 dark:text-purple-400 font-semibold' : 'text-gray-500 dark:text-slate-400'}`}>{s.multaSituacao}</p>
                            ) : null}
                        </Campo>
                    </Secao>

                    <Secao titulo="Rateio e distrato">
                        <Campo rotulo="Situação no rateio">
                            <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md border ${rateioMeta.className}`}>{rateioMeta.icon} {rateioMeta.label}</span>
                        </Campo>
                        <Campo rotulo="Distrato">
                            <span className={`text-[11px] font-bold px-2 py-1 rounded-md border ${distratoMeta.cls}`}>{distratoMeta.label}</span>
                            {s.distratoDaUc ? (
                                <p className="mt-1 flex flex-wrap gap-1">
                                    <span title="Este distrato foi gerado pelo &quot;Gerenciar UC&quot; e não está ligado a este pedido. Vem da unidade, não da solicitação."
                                        className={`${SELO} bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-700 cursor-help`}>
                                        distrato da UC
                                    </span>
                                    {distratoAnteriorAoPedido ? (
                                        <span title="O distrato é anterior à data deste pedido — pode ser de outro cancelamento. Confira no &quot;Gerenciar UC&quot;."
                                            className={`${SELO} bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50 cursor-help`}>
                                            <AlertTriangle size={9} /> anterior ao pedido — conferir
                                        </span>
                                    ) : null}
                                </p>
                            ) : null}
                            {s.distratoNumeroTermo ? <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1 font-mono break-all">{s.distratoNumeroTermo}</p> : null}
                            {s.distratoStatus === 'sem_distrato' && s.distratoExterno ? (
                                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1" title={s.distratoExternoRef || undefined}>
                                    {DISTRATO_EXTERNO_LABEL[s.distratoExterno]}
                                    {s.distratoExternoData ? ` · ${fmtData(s.distratoExternoData)}` : ''}
                                </p>
                            ) : null}
                        </Campo>
                        <Campo rotulo="Conferência">
                            {s.validadoEm ? (
                                <button onClick={onValidar}
                                    title={`${s.validacaoJustificativa ? `${s.validacaoJustificativa}. ` : ''}Clique para rever ou refazer a conferência.`}
                                    className={`${SELO} bg-primary/10 text-primary border-primary/30 hover:bg-primary/20`}>
                                    <ShieldCheck size={10} /> Validado em {new Date(s.validadoEm).toLocaleString('pt-BR')}
                                </button>
                            ) : <span className="text-gray-400 dark:text-slate-500">Ainda não validado</span>}
                        </Campo>
                        <Campo rotulo="Saída do rateio (estimada)">{prazo(s.prazo_saida_rateio_estimado)}</Campo>
                        <Campo rotulo="Uso do saldo (estimado)">{prazo(s.prazo_uso_saldo_estimado)}</Campo>
                    </Secao>

                    {s.respostaEnviadaEm ? (
                        <section className="bg-white dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-700 p-5">
                            <h3 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-4">Conversa com o cliente</h3>
                            <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                                <Campo rotulo="Resposta da Vigor">
                                    {new Date(s.respostaEnviadaEm).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                    <span className="text-xs text-gray-500 dark:text-slate-400">
                                        {' · '}{s.respostaCanal === 'manual'
                                            ? `por fora${s.respostaMeio ? ` (${MEIO_RESPOSTA_LABEL[s.respostaMeio as MeioRespostaManual] ?? s.respostaMeio})` : ''}`
                                            : s.respostaCanal === 'email_whatsapp' ? 'e-mail e WhatsApp' : s.respostaCanal}
                                    </span>
                                </Campo>
                                <Campo rotulo="Retorno do cliente">
                                    {retorno.estado === 'retornou' ? (
                                        <>
                                            <span className="font-semibold">{RETORNO_CLIENTE_LABEL[retorno.tipo] ?? retorno.tipo}</span>
                                            <span className="text-xs text-gray-500 dark:text-slate-400">
                                                {' · '}{new Date(s.clienteRetornoEm!).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                            </span>
                                            {s.clienteRetornoObs ? <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 whitespace-pre-wrap">{s.clienteRetornoObs}</p> : null}
                                        </>
                                    ) : retorno.estado === 'aguardando' ? (
                                        <span className={`${SELO} ${retorno.atrasado
                                            ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/50'
                                            : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50'}`}
                                            title={`Sem retorno do cliente desde a resposta da Vigor. Fica vermelho a partir de ${DIAS_ALERTA_SEM_RETORNO_CLIENTE} dias — só avisa, o processo continua.`}>
                                            <Clock size={9} /> Aguardando há {retorno.dias} dia{retorno.dias === 1 ? '' : 's'}
                                        </span>
                                    ) : <span className="text-gray-400 dark:text-slate-500">—</span>}
                                </Campo>
                            </dl>
                            {!registrandoRetorno ? (
                                <button type="button" onClick={() => setRegistrandoRetorno(true)}
                                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:opacity-80">
                                    <MessageSquareText size={12} /> {retorno.estado === 'retornou' ? 'Atualizar retorno do cliente' : 'Registrar retorno do cliente'}
                                </button>
                            ) : (
                                <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label htmlFor="retorno-cliente-tipo" className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1">O que o cliente disse</label>
                                            <select id="retorno-cliente-tipo" value={tipoRetorno} onChange={e => setTipoRetorno(e.target.value as TipoRetornoCliente)}
                                                className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 focus:border-primary outline-none">
                                                <option value="">Selecione...</option>
                                                {(['confirmou', 'desistiu', 'negociar', 'outro'] as TipoRetornoCliente[]).map(t => (
                                                    <option key={t} value={t}>{RETORNO_CLIENTE_LABEL[t]}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor="retorno-cliente-data" className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1">Quando</label>
                                            <input id="retorno-cliente-data" type="date" value={dataRetorno} min={dataMinRetorno} max={hojeSP()}
                                                onChange={e => setDataRetorno(e.target.value)}
                                                className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 focus:border-primary outline-none" />
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="retorno-cliente-obs" className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                                            Observação {tipoRetorno === 'outro' ? '*' : '(opcional)'}
                                        </label>
                                        <textarea id="retorno-cliente-obs" rows={2} value={obsRetorno} onChange={e => setObsRetorno(e.target.value)}
                                            placeholder="Resumo do que o cliente respondeu..."
                                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 focus:border-primary outline-none resize-y" />
                                    </div>
                                    {tipoRetorno === 'desistiu' ? (
                                        <p className="text-[11px] text-gray-500 dark:text-slate-400">
                                            Isto só registra o que o cliente disse. Para encerrar o pedido, use "Desistência" no rodapé.
                                        </p>
                                    ) : null}
                                    <div className="flex items-center justify-end gap-2">
                                        <button type="button" onClick={() => setRegistrandoRetorno(false)} disabled={salvandoRetorno}
                                            className="px-3 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800">
                                            Voltar
                                        </button>
                                        <button type="button" onClick={salvarRetorno}
                                            disabled={salvandoRetorno || !tipoRetorno || (tipoRetorno === 'outro' && !obsRetorno.trim())}
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">
                                            <CheckCircle2 size={14} /> {salvandoRetorno ? 'Registrando...' : 'Registrar retorno'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </section>
                    ) : null}
                </div>

                <div className="px-6 py-4 border-t border-gray-100 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3 shrink-0 bg-white dark:bg-slate-800">
                    <button onClick={e => onHistorico(e.currentTarget.getBoundingClientRect())}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200">
                        <History size={14} /> Histórico
                    </button>
                    <div className="flex flex-wrap items-center gap-2">
                        {acoes.podeDesistir ? (
                            <button onClick={onDesistir} className={`${botaoAcao} border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700`}>
                                <XCircle size={15} /> Desistência
                            </button>
                        ) : null}
                        {acoes.podeConcluir ? (
                            <button onClick={onConcluir} className={`${botaoAcao} border border-emerald-200 dark:border-emerald-800/50 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20`}>
                                <CheckCircle2 size={15} /> Concluir
                            </button>
                        ) : null}
                        {acoes.podeResponder ? (
                            <button onClick={onResponder}
                                title={acoes.rotuloResposta === 'reenviar' ? env.explicacao
                                    : acoes.rotuloResposta === 'respondida' ? 'Cliente já respondido. Clique para rever ou responder de novo.'
                                        : 'Monta a mensagem de retorno com os valores da conferência'}
                                className={`${botaoAcao} ${acoes.rotuloResposta === 'reenviar'
                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                    : acoes.rotuloResposta === 'respondida'
                                        ? 'border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                                        : 'border border-primary/30 text-primary hover:bg-primary/10'}`}>
                                {acoes.rotuloResposta === 'reenviar' ? <AlertTriangle size={15} />
                                    : acoes.rotuloResposta === 'respondida' ? <CheckCircle2 size={15} /> : <MessageSquareText size={15} />}
                                {acoes.rotuloResposta === 'reenviar' ? 'Reenviar resposta' : acoes.rotuloResposta === 'respondida' ? 'Resposta enviada' : 'Responder cliente'}
                            </button>
                        ) : null}
                        {acoes.podeValidar ? (
                            <button onClick={onValidar} className={`${botaoAcao} bg-primary text-white hover:opacity-90`}>
                                <ShieldCheck size={15} /> Validar cancelamento
                            </button>
                        ) : null}
                        {acoes.podeGerarDistrato ? (
                            <button onClick={onGerarDistrato} className={`${botaoAcao} bg-primary text-white hover:opacity-90`}>
                                <FileText size={15} /> Gerar distrato
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default SolicitacaoCancelamentoModal;
